/**
 * RequesterStatusBanner - Banner shown to PMs/Collaborators who have requested an upgrade
 * Shows the status of their request and allows them to send reminders or re-request
 */

import React, { useState, useEffect } from 'react';
import { Clock, Bell, RefreshCw, X, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { billingApi } from '../../services/api';
import { toast } from 'sonner';

const RequesterStatusBanner = ({ className }) => {
  const [request, setRequest] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  
  useEffect(() => {
    fetchMyRequest();
  }, []);
  
  const fetchMyRequest = async () => {
    try {
      const response = await billingApi.getMyUpgradeRequest();
      if (response.data?.has_request) {
        setRequest(response.data.request);
      }
    } catch (error) {
      console.error('Failed to fetch upgrade request status:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSendReminder = async () => {
    setActionLoading(true);
    try {
      await billingApi.sendUpgradeReminder();
      toast.success('Reminder sent! 📧', {
        description: 'The organization owner has been notified.',
      });
      // Update local state
      setRequest(prev => ({
        ...prev,
        last_reminder_at: new Date().toISOString(),
        reminder_count: (prev?.reminder_count || 0) + 1
      }));
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send reminder';
      toast.error(message);
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleDismiss = async () => {
    setActionLoading(true);
    try {
      await billingApi.dismissMyUpgradeRequest();
      setRequest(null);
      toast.info('Request dismissed');
    } catch (error) {
      console.error('Failed to dismiss request:', error);
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleReRequest = async () => {
    setActionLoading(true);
    try {
      await billingApi.reRequestUpgrade();
      toast.success('Upgrade re-requested! 📧', {
        description: 'A new request has been sent to the organization owner.',
      });
      // Refresh to get updated request
      await fetchMyRequest();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to re-request';
      toast.error(message);
    } finally {
      setActionLoading(false);
    }
  };
  
  if (isLoading || !request) return null;
  
  // Calculate time since request
  const requestDate = new Date(request.created_at);
  const now = new Date();
  const hoursSinceRequest = Math.floor((now - requestDate) / (1000 * 60 * 60));
  const daysSinceRequest = Math.floor(hoursSinceRequest / 24);
  
  // Check if can send reminder (24 hours since last reminder or initial request)
  const lastReminderDate = request.last_reminder_at ? new Date(request.last_reminder_at) : requestDate;
  const hoursSinceLastReminder = Math.floor((now - lastReminderDate) / (1000 * 60 * 60));
  const canSendReminder = hoursSinceLastReminder >= 24;
  
  // Check if request is expired (7 days)
  const isExpired = daysSinceRequest >= 7;
  
  // Check if request was resolved
  const isResolved = request.status === 'resolved' || request.status === 'approved';
  const isDeclined = request.status === 'declined';
  
  // Determine banner style based on status
  let bannerStyle = 'from-blue-600 to-indigo-600'; // Default pending
  let icon = Clock;
  let statusText = 'Pending';
  
  if (isExpired && request.status === 'pending') {
    bannerStyle = 'from-amber-500 to-orange-500';
    icon = AlertTriangle;
    statusText = 'Expired';
  } else if (isResolved) {
    bannerStyle = 'from-green-500 to-emerald-500';
    icon = CheckCircle;
    statusText = 'Approved';
  } else if (isDeclined) {
    bannerStyle = 'from-red-500 to-rose-500';
    icon = X;
    statusText = 'Declined';
  }
  
  const Icon = icon;
  
  // Format time display
  const timeDisplay = daysSinceRequest > 0 
    ? `${daysSinceRequest} day${daysSinceRequest > 1 ? 's' : ''} ago`
    : hoursSinceRequest > 0 
      ? `${hoursSinceRequest} hour${hoursSinceRequest > 1 ? 's' : ''} ago`
      : 'Just now';
  
  return (
    <div className={cn(
      `bg-gradient-to-r ${bannerStyle} text-white px-4 py-2`,
      className
    )}>
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5" />
          <div className="text-sm">
            {isExpired && request.status === 'pending' ? (
              <span>
                Your upgrade request has <span className="font-semibold">expired</span> after 7 days.
                Would you like to request again?
              </span>
            ) : isResolved ? (
              <span>
                🎉 Your upgrade request was <span className="font-semibold">approved</span>! 
                You now have access to more features.
              </span>
            ) : isDeclined ? (
              <span>
                Your upgrade request was declined. 
                Contact your organization owner for more information.
              </span>
            ) : (
              <span>
                Your upgrade request is <span className="font-semibold">{statusText.toLowerCase()}</span>
                <span className="opacity-80 ml-1">• Requested {timeDisplay}</span>
                {request.reminder_count > 0 && (
                  <span className="opacity-70 ml-1">
                    • {request.reminder_count} reminder{request.reminder_count > 1 ? 's' : ''} sent
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Actions based on status */}
          {isExpired && request.status === 'pending' ? (
            <>
              <button
                onClick={handleReRequest}
                disabled={actionLoading}
                className="flex items-center gap-1 px-3 py-1 bg-white/20 hover:bg-white/30 rounded-md text-sm font-medium transition-colors"
              >
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Re-request
              </button>
              <button
                onClick={handleDismiss}
                disabled={actionLoading}
                className="p-1 hover:bg-white/20 rounded-md transition-colors"
                title="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </>
          ) : isResolved || isDeclined ? (
            <button
              onClick={handleDismiss}
              disabled={actionLoading}
              className="flex items-center gap-1 px-3 py-1 bg-white/20 hover:bg-white/30 rounded-md text-sm font-medium transition-colors"
            >
              Dismiss
            </button>
          ) : (
            <>
              {canSendReminder ? (
                <button
                  onClick={handleSendReminder}
                  disabled={actionLoading}
                  className="flex items-center gap-1 px-3 py-1 bg-white/20 hover:bg-white/30 rounded-md text-sm font-medium transition-colors"
                >
                  {actionLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Bell className="w-4 h-4" />
                  )}
                  Send Reminder
                </button>
              ) : (
                <span className="text-xs opacity-70 px-2">
                  Can remind in {24 - hoursSinceLastReminder}h
                </span>
              )}
              <button
                onClick={handleDismiss}
                disabled={actionLoading}
                className="p-1 hover:bg-white/20 rounded-md transition-colors"
                title="Cancel request"
              >
                <X className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequesterStatusBanner;
